import React, { forwardRef, useImperativeHandle, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useCreateTitle from './useCreateTitle';
import CreateForm, { specForId } from './CreateForm';
import ProductionCreateForm from './ProductionCreateForm';
import ConsumptionCreateForm from './ConsumptionCreateForm';
import MortalityCreateForm from './MortalityCreateForm';
import BandBatchCreateForm from './BandBatchCreateForm';
import TransferCreateForm from './TransferCreateForm';
import OpeningCreateForm from './OpeningCreateForm';
import AdjustmentCreateForm from './AdjustmentCreateForm';
import ParametersCreateForm from './ParametersCreateForm';
import SalesCreateForm from './SalesCreateForm';
import PurchaseCreateForm from './PurchaseCreateForm';
import ExpenseCreateForm from './ExpenseCreateForm';
import OtherRevenueCreateForm from './OtherRevenueCreateForm';
import PaymentCreateForm from './PaymentCreateForm';
import LandedCreateForm from './LandedCreateForm';
import CreditNoteCreateForm from './CreditNoteCreateForm';
import CreditNoteDoorForm from './CreditNoteDoorForm';
import { GoodwillCreateForm, CompensationCreateForm } from './GoodwillCreateForm';
import SaleFeesCreateForm from './SaleFeesCreateForm';
import DiscountDoorForm from './DiscountDoorForm';
import DispatchDoorForm from './DispatchDoorForm';
import ReceiptCreateForm from './ReceiptCreateForm';
import DrawerFrame from './drawer/DrawerFrame';
import { ConfirmModal } from './drawer/ShellModal';
import useActionLabel from '../i18n/useActionLabel';
import './createForms.css';

import { emoText } from './EmojiIcons';
// forms wired to real data + save (depth-first); the rest fall back to the mock CreateForm renderer.
const WIRED = {
  'by-band': BandBatchCreateForm,
  production: ProductionCreateForm, consumption: ConsumptionCreateForm, mortality: MortalityCreateForm, parameters: ParametersCreateForm,
  transfer: TransferCreateForm, opening: OpeningCreateForm, adjustment: AdjustmentCreateForm,
  sales: SalesCreateForm, 'sale-fees': SaleFeesCreateForm, discount: DiscountDoorForm, dispatch: DispatchDoorForm, purchase: PurchaseCreateForm,
  expense: ExpenseCreateForm, 'other-revenue': OtherRevenueCreateForm, payment: PaymentCreateForm,
  'credit-note': CreditNoteCreateForm,   // fb-143
  'credit-note-customer': CreditNoteDoorForm,   // 47204: the ONE customer credit-note door (picker when no order context > CreditNoteBody)
  goodwill: GoodwillCreateForm, compensation: CompensationCreateForm,   // v0.46 AVOIR Phase 3 (customer page doors, switch ON + admin)
  receipt: ReceiptCreateForm,   // 45138: Record Receipt (global, order picker; see Prompts/0.45/45138_receipt_drawer_CONTRACT.md)
  landed: LandedCreateForm,   // fb-37 (43027): wired — preview→confirm, Model A holding, catch-up on received units.
};

// Create-drawer host (fb-37): the + Create menu opens each data-entry form as a right-side drawer whose
// body is the MOCK transposed (CreateForm), not the legacy page. ONE draft at a time: switching to a
// different form while the current one is DIRTY prompts to discard; pristine forms swap/close silently;
// a dirty form that's closed collapses to a compact top-right pill (Go to form / Discard) and stays
// mounted so its draft survives until Save or Discard. The ↗ tool opens the standalone full page (kept
// alive for diffing). Dirty = any interaction since open.
// QUOTE-EDIT: a draft is identified by its form id PLUS the quote it edits/converts and the mode, so "Edit
// quote" on another quote, "Convert to order" on the same one, or a new sale is a different draft.
const draftKey = (id, params) => id + (params && params.quote ? ':' + params.quote.operation_id + ':' + (params.quoteMode || 'edit')
  : params && params.duplicate ? ':dup:' + params.duplicate.operation_id : id === 'payment' && params && params.subtype ? ':' + params.subtype
  : id === 'receipt' ? ':' + ((params && params.operationId) || '') + (params && params.scopeLineId ? ':' + params.scopeLineId : '')   // a receipt for another order / line is another draft
  : id === 'goodwill' || id === 'compensation' ? ':' + ((params && params.partnerId) || '')   // one draft per customer
  : id === 'credit-note-customer' ? ':' + ((params && (params.operationId || params.partner_id)) || '') + ((params && params.draftId) ? '|' + params.draftId : '')   // a credit note for another order / customer / draft is another draft
  : id === 'dispatch' ? ':' + ((params && (params.operationId || params.partnerId)) || '') : '');   // Supplier vs Customer payment, and a dispatch for another order / customer, are different drafts

const CreateDrawerHost = forwardRef(function CreateDrawerHost(_props, ref) {
  const navigate = useNavigate();
  const { t } = useTranslation('dataEntry');
  const ct = useCreateTitle();
  const nameOf = (c) => ct(c.id, c.params) || specForId(c.id).title;
  const [cur, setCur] = useState(null);          // { id, route } | null — the active draft
  const [min, setMin] = useState(false);
  const [size, setSize] = useState('expanded');  // 'expanded' (720, owner ruling: create drawers stay 720) | 'standard' (456)
  const al = useActionLabel();
  const [confirm, setConfirm] = useState(null);  // { id, route } | null — pending switch awaiting discard
  const dirty = useRef(false);
  const [label, setLabel] = useState('');       // the draft's own name for the pill (e.g. the chosen order id)

  const openForm = useCallback((id, route, params) => { setCur({ id, route, params }); setMin(false); setSize('expanded'); setConfirm(null); dirty.current = false; setLabel(''); }, []);
  const doOpen = useRef(null);   // always the latest closure over cur (the imperative handle below is created once)
  doOpen.current = (id, route, params) => {
    if (!cur) { openForm(id, route, params); return; }
    if (draftKey(id, params) === draftKey(cur.id, cur.params)) { setMin(false); return; }   // same draft → restore it
    if (!dirty.current) { openForm(id, route, params); return; } // pristine → swap silently
    setConfirm({ id, route, params });                           // dirty → ask before discarding
  };
  useImperativeHandle(ref, () => ({
    open(id, route, params) {
      if (!id || !specForId(id)) return;
      doOpen.current(id, route, params);
    },
  }), []);

  const discard = () => { setCur(null); setMin(false); setConfirm(null); dirty.current = false; setLabel(''); };
  const requestClose = () => { if (dirty.current) setMin(true); else discard(); };
  const fullPage = () => { const r = cur && cur.route; discard(); if (r) navigate(`${r}?shell=v2`); };

  if (!cur) return null;
  const spec = specForId(cur.id);
  const nextSpec = confirm && { ...specForId(confirm.id), title: nameOf(confirm) };
  const title = nameOf(cur);
  const Wired = WIRED[cur.id];
  const shared = { size, onSize: () => setSize(s => (s === 'expanded' ? 'standard' : 'expanded')), onFullPage: fullPage, onStocktake: () => { discard(); navigate('/inventory/stocktake?shell=v2'); }, onClose: requestClose, onInteract: (d, l) => { dirty.current = d !== false; if (d === false) setLabel(''); else if (l) setLabel(l); }   /* fb-177: forms call onInteract(false) when a save leaves them empty */, params: cur.params };
  const after = (
    <>
      {min && <div className="cf-pill" role="group" aria-label={t('fl.drawer_draft_minimized', { title })}>
        <div className="cp-r1"><span className="cp-ic">{emoText(spec.ini)}</span><span className="cp-t">{title}{label ? ' · ' + label : ''}</span></div>
        <div className="cp-r2">
          <button type="button" className="cp-btn" onClick={() => setMin(false)}>{t('fl.go_to_form')}</button>
          <button type="button" className="cp-btn discard" onClick={discard}>{al('discard')}</button>
        </div>
      </div>}

      {confirm && <ConfirmModal title={t('fl.drawer_discard_title', { title })} message={t('fl.drawer_discard_body', { next: nextSpec && nextSpec.title })} cancelLabel={t('fl.keep_editing')} confirmLabel={t('fl.drawer_discard_start', { next: nextSpec && nextSpec.title })} onCancel={() => setConfirm(null)} onConfirm={() => openForm(confirm.id, confirm.route, confirm.params)} />}
    </>
  );
  return (
    <DrawerFrame kind="create" portal size={size} open={!min} onClose={confirm ? () => setConfirm(null) : requestClose} label={t('fl.drawer_create_title', { title })} after={after}>
      {Wired
        ? <Wired key={draftKey(cur.id, cur.params)} {...shared} />
        : <CreateForm key={cur.id} spec={spec} {...shared} />}
    </DrawerFrame>
  );
});
export default CreateDrawerHost;
